import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { startPwa } from "@/pwa/runtime";
import { PwaOfflineNotice } from "@/pwa/PwaUi";
import { OfflineSyncBridge } from "@/offline/bridge";
import { PwaPushBridge } from "@/pwa/PushUi";
import { App } from "@/app/App";
import { BootScreen, BootSplashCleanup } from "@/app/BootLoader";
import { ErrorBoundary } from "@/app/ErrorBoundary";
import { BrandingProvider } from "@/app/branding";
import { ToastProvider } from "@/app/toast";
import { AuthProvider } from "@/app/auth";
import { recoverStaleBuild } from "@/app/staleBuild";

import i18n from "@/i18n";
import { ApiError, setApiLanguage } from "@/api/client";
import { applyStylesheet } from "@/styles/direction";

startPwa();
setApiLanguage(i18n.language);
applyStylesheet(i18n.dir(i18n.language));
i18n.on("languageChanged", (code) => {
    setApiLanguage(code);
    applyStylesheet(i18n.dir(code));
});

// a screen of an older build failed to load: the app was rebuilt since this tab opened
window.addEventListener("vite:preloadError", (event) => {
    if (recoverStaleBuild()) {
        event.preventDefault();
    }
});

/** A call that may well work a moment later: the network dropped it, or the
 *  server was busy or restarting. A refusal (4xx) is final and is not retried. */
function transient(error: unknown): boolean {
    if (!(error instanceof ApiError)) {
        return !(error instanceof Error && error.name === "AbortError");
    }
    return error.status >= 500 || error.status === 429 || error.status === 0;
}

const RELOAD_MARK = "pm-session-reload";

/** The server no longer knows the session (it was rotated or expired while the
 *  tab was open): reloading signs in again through the identity provider. Once
 *  a minute at most, so a sign-in that keeps failing cannot loop. */
function renewSession(error: unknown) {
    if (!(error instanceof ApiError) || error.status !== 401 || error.code !== "session_expired") {
        return;
    }
    try {
        const last = Number(sessionStorage.getItem(RELOAD_MARK) || 0);
        if (Date.now() - last < 60_000) {
            return;
        }
        sessionStorage.setItem(RELOAD_MARK, String(Date.now()));
    } catch {
        return;
    }
    window.location.reload();
}

const LegacyOfflineRedirect = lazy(() => import("@/offline/LegacyRedirect").then(module => ({ default: module.LegacyRedirect })));
const VerificationScreen = lazy(() => import("@/features/verification/VerificationScreen").then((module) => ({ default: module.VerificationScreen })));

const client = new QueryClient({
    queryCache: new QueryCache({ onError: renewSession }),
    mutationCache: new MutationCache({ onError: renewSession }),
    defaultOptions: {
        mutations: { networkMode: "always", retry: false },
        queries: {
            retry: (failures, error) => failures < 2 && transient(error),
            retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 4000),
            staleTime: 30_000,
            refetchOnWindowFocus: false,
        },
    },
});

createRoot(document.getElementById("root") as HTMLElement).render(
    <StrictMode>
        <QueryClientProvider client={client}>
            <BrowserRouter>
                <BootSplashCleanup />
                <Routes>
                    <Route path="/offline" element={<Suspense fallback={<BootScreen />}><LegacyOfflineRedirect /></Suspense>} />
                    <Route path="/verify" element={<Suspense fallback={<BootScreen />}><VerificationScreen /></Suspense>} />
                    <Route path="/verify/:token" element={<Suspense fallback={<BootScreen />}><VerificationScreen /></Suspense>} />
                    <Route path="*" element={<BrandingProvider>
                    <PwaOfflineNotice />
                    <ToastProvider>
                        <AuthProvider>
                            <PwaPushBridge />
                            <OfflineSyncBridge />
                            <ErrorBoundary>
                                <App />
                            </ErrorBoundary>
                        </AuthProvider>
                    </ToastProvider>
                </BrandingProvider>} />
                </Routes>
            </BrowserRouter>
        </QueryClientProvider>
    </StrictMode>,
);
